var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { DlqIgnoreRules } from 'commbot-shared-types';
import { v4 } from 'uuid';
export const buildDefaultIgnoreRulesViewState = () => ({
    view: 'LOADING',
    ignoreRules: [],
});
const parseMatcher = (match) => {
    if (typeof match === 'object') {
        if (match.pattern) {
            return { matcherType: 'pattern', matchVal: match.pattern, matchFlag: match.flags };
        }
        if (match.isUndefined) {
            return { matcherType: 'isUndefined', matchVal: 'true' };
        }
        if (match.isNull) {
            return { matcherType: 'isNull', matchVal: 'true' };
        }
        if (match.isFalsy) {
            return { matcherType: 'isFalsy', matchVal: 'true' };
        }
        if (match.isTruthy) {
            return { matcherType: 'isTruthy', matchVal: 'true' };
        }
        if (match.isEmpty) {
            return { matcherType: 'isEmpty', matchVal: 'true' };
        }
    }
    else {
        return { matcherType: 'primitive', matchVal: match.toString() };
    }
    throw new Error(`dont recognise matcher: ${JSON.stringify(match, null, 2)}`);
};
const buildMatcher = ({ key, matchVal, matcherType, matchFlag: flags }) => {
    switch (matcherType) {
        case 'primitive':
            if (!matchVal) {
                throw new Error(`match is required when match type is primitive`);
            }
            return {
                key,
                match: matchVal,
            };
        case 'pattern':
            if (!matchVal) {
                throw new Error(`match is required when match type is pattern`);
            }
            return { key, match: { flags, pattern: matchVal } };
        case 'isFalsy':
        case 'isTruthy':
        case 'isNull':
        case 'isUndefined':
        case 'isEmpty':
            return { key, match: { [matcherType]: true } };
        default:
            throw new Error(`unrecognised userEnteredType: ${matcherType}`);
    }
};
export const buildEmptyGenericMatcherFields = (ruleType) => ({
    key: ruleType === 'FARKEN-ERROR' ? 'msg' : '',
    matcherType: 'pattern',
    matchVal: '',
    matchFlag: '',
});
export const buildEmptyModstepMatcherFields = () => (Object.assign({ stepName: '' }, buildEmptyGenericMatcherFields()));
export const buildEmptyModplanMatcherFields = () => ({
    modInfoInputs: [],
    modStepInputs: [],
});
const buildGenericMatchersFieldsArray = (ignoreRules) => {
    if (!ignoreRules || DlqIgnoreRules.isModPlanMatcherExpressions(ignoreRules)) {
        return [];
    }
    return ignoreRules.map(({ key, match }) => (Object.assign({ key }, parseMatcher(match))));
};
const buildModplanMatchersFieldsArray = (ignoreRules) => {
    if (!ignoreRules || !DlqIgnoreRules.isModPlanMatcherExpressions(ignoreRules)) {
        return [];
    }
    return ignoreRules.map(({ modInfo, modSteps }) => ({
        modInfoInputs: (modInfo || []).map(({ key, match }) => (Object.assign({ key }, parseMatcher(match)))),
        modStepInputs: (modSteps || []).map(({ key, match, stepName }) => (Object.assign({ stepName, key }, parseMatcher(match)))),
    }));
};
export const toFormInputs = (r) => ({
    id: (r === null || r === void 0 ? void 0 : r.id) || v4(),
    description: (r === null || r === void 0 ? void 0 : r.description) || '',
    dlqName: (r === null || r === void 0 ? void 0 : r.dlqName) || '',
    env: (r === null || r === void 0 ? void 0 : r.env) || 'ALL',
    region: (r === null || r === void 0 ? void 0 : r.region) || 'ALL',
    ignoreRulesType: DlqIgnoreRules.isModPlanMatcherExpressions(r === null || r === void 0 ? void 0 : r.ignoreRules)
        ? 'MODPLAN'
        : (r === null || r === void 0 ? void 0 : r.dlqName.toLocaleUpperCase()) === 'FARKEN-ERRORS'
            ? 'FARKEN-ERROR'
            : 'GENERIC',
    genericMatchers: buildGenericMatchersFieldsArray(r === null || r === void 0 ? void 0 : r.ignoreRules),
    modplanMatchers: buildModplanMatchersFieldsArray(r === null || r === void 0 ? void 0 : r.ignoreRules),
});
const buildModPlanIgnoreRulesFromInputs = (inputs) => {
    const result = inputs.map(({ modInfoInputs, modStepInputs }) => ({
        modInfo: modInfoInputs.length ? modInfoInputs.map(buildMatcher) : undefined,
        modSteps: modStepInputs.length
            ? modStepInputs.map((_a) => {
                var { stepName } = _a, d = __rest(_a, ["stepName"]);
                return (Object.assign({ stepName }, buildMatcher(d)));
            })
            : undefined,
    }));
    if (!result.length) {
        throw new Error(`A modplan matcher is required`);
    }
    for (const { modInfo, modSteps } of result) {
        if ((!modInfo || !modInfo.length) && (!modSteps || !modSteps.length)) {
            throw new Error(`Each modplan matcher must have at least one ModInfo or ModStep matcher`);
        }
    }
    return result;
};
const buildGenericIgnoreRuleFromInputs = (inputs) => {
    const result = inputs.map(buildMatcher);
    if (!result.length) {
        throw new Error(`A matcher is required`);
    }
    return result;
};
export const fromFormInputs = (_a) => {
    var { ignoreRulesType, genericMatchers, modplanMatchers, dlqName } = _a, props = __rest(_a, ["ignoreRulesType", "genericMatchers", "modplanMatchers", "dlqName"]);
    return (Object.assign(Object.assign({}, props), { dlqName: dlqName.toLocaleUpperCase(), logsMustExistPatterns: undefined, ignoreRules: ignoreRulesType === 'MODPLAN'
            ? buildModPlanIgnoreRulesFromInputs(modplanMatchers)
            : buildGenericIgnoreRuleFromInputs(genericMatchers) }));
};
export const mergeIgnoreRule = (ignoreRules, dlqIgnoreRuleRecord) => {
    let updatedIgnoreRules = ignoreRules;
    const index = ignoreRules.findIndex((r) => r.id === dlqIgnoreRuleRecord.id);
    if (index >= 0) {
        updatedIgnoreRules[index] = dlqIgnoreRuleRecord;
    }
    else {
        updatedIgnoreRules = [...updatedIgnoreRules, dlqIgnoreRuleRecord];
    }
    return updatedIgnoreRules;
};
//# sourceMappingURL=util.js.map